import React, { FC, useEffect, useState } from 'react'
import { Card, Checkbox, Col, Form, FormProps, InputNumber, notification, Row, Tooltip } from 'antd'
import RSaveOverlay from '../../../components/RSaveOverlay'
import RDragger from '../../../components/RUpload'
import { IScenario, IScenarioInitialValues, IScenarioPost } from '../../../types/scenario'
import { modelApi as modelApiInstance } from '../../../api/apiInstances'
import { storageApi } from '../../../api'
import { IFilePostSuccesResponse } from '../../../types/file'
import { UploadChangeParam, UploadFile } from 'antd/lib/upload'
import { normFile } from '../../../utils'
import PFC_XLSX from '../../../assets/files/pfc.xlsx'
import LOAD_XLSX from '../../../assets/files/load.xlsx'
import DAY_AHEAD_XLSX from '../../../assets/files/day-ahead.xlsx'
import LabelWithTemplate from './LabelWithTablate'

type IGridConnection = { label: string; value: string }

const GRID_CONNECTIONS: Record<string, IGridConnection> = {
  SELL_TO_GRID: { label: 'Sell To Grid', value: 'sellToGrid' },
  PURCHASE_FROM_GRID: { label: 'Purchase From Grid', value: 'purchaseFromFrom' },
  // TODO: rename from from
}

interface Props extends FormProps {
  displayOverlayTriggerButton?: boolean
  initialValues?: IScenarioInitialValues
}

const SceneriosForm: FC<Props> = ({
  initialValues,
  displayOverlayTriggerButton = false,
  ...formProps
}) => {
  const [form] = Form.useForm()
  const [savedScenarios, setSavedScenarios] = useState<IScenario[]>([])
  const [timespan, setTimespan] = useState<number>()
  const [isTimeSpanDisabled, setIsTimeSpanDisabled] = useState(false)
  // const [scenario, setScenario] = useState<IScenarioFiles>({
  //   dayAheadPrices: '',
  //   primaryFrequencyPrices: '',
  //   loadForecast: '',
  //   generationProjection: '',
  // })
  const formInitialValues = initialValues || {}

  async function fetchScenarios() {
    const scenarios = await storageApi.fetchScenarios()
    setSavedScenarios(scenarios)
  }

  async function saveScenario(saveName: string) {
    try {
      const {
        sellToGrid,
        purchaseFromFrom,
        storageSellPriceLimitDollars,
        dayAheadPrices,
        primaryFrequencyPrices,
        loadForecast,
        timeSpan,
      }: IScenarioInitialValues = form.getFieldsValue()

      await storageApi.createScenario({
        name: saveName,
        sellToGrid: sellToGrid || false,
        purchaseFromFrom: purchaseFromFrom || false,
        storageSellPriceLimitDollars,
        dayAheadPrices: dayAheadPrices?.[0]?.url || '',
        primaryFrequencyPrices: primaryFrequencyPrices?.[0]?.url || '',
        loadForecast: loadForecast?.[0]?.url || '',
        timeSpan,
      })
      notification.success({ message: 'Scenario has been saved successfully' })
    } catch (error) {
      console.log(error)
    } finally {
      fetchScenarios()
    }
  }

  function setFileDataIfResponseSuccessfull(
    formItemName: keyof IScenarioPost,
    fileResponse: UploadChangeParam<UploadFile<IFilePostSuccesResponse>>,
  ) {
    if (fileResponse.file.error || !fileResponse.fileList.length) return
    if (!fileResponse.file.response?.file_link) return

    const file = form.getFieldValue(formItemName)[0]
    const downloadUrl = fileResponse.file.response?.file_link

    form.setFieldsValue({ [formItemName]: [{ ...file, url: downloadUrl }] })
    updateIsTimeSpanDisabled()

    if (formProps.onValuesChange) {
      formProps.onValuesChange(null, form.getFieldsValue())
    }
  }

  function updateIsTimeSpanDisabled() {
    if (form.getFieldValue('dayAheadPrices')) {
      setIsTimeSpanDisabled(true)
    } else {
      setIsTimeSpanDisabled(false)
    }
  }

  function loadScenario(savedScenarioIdx: number) {
    const {
      dayAheadPrices,
      primaryFrequencyPrices,
      loadForecast,
      purchaseFromFrom,
      sellToGrid,
      storageSellPriceLimitDollars,
      timeSpan,
    } = savedScenarios[savedScenarioIdx]
    const files = { dayAheadPrices, primaryFrequencyPrices, loadForecast }

    form.resetFields()
    form.setFieldsValue({ purchaseFromFrom, sellToGrid, storageSellPriceLimitDollars, timeSpan })
    Object.entries(files).forEach(([fileKey, fileUrl], idx) => {
      if (fileUrl) {
        form.setFieldsValue({ [fileKey]: [{ uid: idx, name: fileUrl, url: fileUrl }] })
      }
    })
    setTimespan(timeSpan)
    updateIsTimeSpanDisabled()
    formProps.onValuesChange?.(form.getFieldsValue(), form.getFieldsValue())
  }

  useEffect(() => {
    fetchScenarios()
    updateIsTimeSpanDisabled()
  }, [])

  return (
    <RSaveOverlay
      displayOverlayTriggerButton={displayOverlayTriggerButton}
      loadDataOptions={savedScenarios.map((scenario, idx) => ({
        label: scenario.name,
        value: idx,
      }))}
      onLoadData={(savedScenarioIdx) => loadScenario(savedScenarioIdx)}
      onSave={saveScenario}
    >
      <Card className='ticket-primary'>
        <Form form={form} layout='vertical' initialValues={formInitialValues} {...formProps}>
          <Row gutter={40}>
            <Col>
              <Form.Item name={GRID_CONNECTIONS.SELL_TO_GRID.value} valuePropName='checked'>
                <Checkbox key={GRID_CONNECTIONS.SELL_TO_GRID.value}>
                  {GRID_CONNECTIONS.SELL_TO_GRID.label}
                </Checkbox>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name={GRID_CONNECTIONS.PURCHASE_FROM_GRID.value} valuePropName='checked'>
                <Checkbox key={GRID_CONNECTIONS.PURCHASE_FROM_GRID.value}>
                  {GRID_CONNECTIONS.PURCHASE_FROM_GRID.label}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={40}>
            <Col span={12}>
              <Tooltip title={isTimeSpanDisabled ? "Delete 'Day Ahead Prices' file first" : ''}>
                <Form.Item label='Time Delta' name='timeSpan'>
                  <InputNumber
                    className='w-100'
                    addonAfter='Hours'
                    onInput={(val) => setTimespan(parseInt(val, 10))}
                    disabled={isTimeSpanDisabled}
                  />
                </Form.Item>
              </Tooltip>
              <Form.Item label='Storage Sell Price Limit' name='storageSellPriceLimitDollars'>
                <InputNumber className='w-100' addonAfter='$' />
              </Form.Item>
            </Col>
            <Col span={8} />
          </Row>

          <Row gutter={40}>
            <Col span={24}>
              <Tooltip title={timespan ? '' : 'Time Delta is required'}>
                <Form.Item
                  label={<LabelWithTemplate label='Day Ahead Prices' template={DAY_AHEAD_XLSX} />}
                  name='dayAheadPrices'
                  valuePropName='fileList'
                  getValueFromEvent={normFile}
                >
                  <RDragger
                    disabled={!timespan}
                    action={`${modelApiInstance.baseURL}/file/dayaheadprices/1`}
                    onChange={(info) => {
                      setFileDataIfResponseSuccessfull('dayAheadPrices', info)
                    }}
                    onRemove={() => setIsTimeSpanDisabled(false)}
                  />
                </Form.Item>
              </Tooltip>
            </Col>

            <Col span={24}>
              <Form.Item
                label={<LabelWithTemplate label='Primary Frequency Prices' template={PFC_XLSX} />}
                name='primaryFrequencyPrices'
                valuePropName='fileList'
                getValueFromEvent={normFile}
              >
                <RDragger
                  action={`${modelApiInstance.baseURL}/file/primaryfrequencyprices`}
                  onChange={(info) => {
                    setFileDataIfResponseSuccessfull('primaryFrequencyPrices', info)
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={<LabelWithTemplate label='Load Forecasts' template={LOAD_XLSX} />}
                name='loadForecast'
                valuePropName='fileList'
                getValueFromEvent={normFile}
              >
                <RDragger
                  action={`${modelApiInstance.baseURL}/file/forecast`}
                  onChange={(info) => {
                    setFileDataIfResponseSuccessfull('loadForecast', info)
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </RSaveOverlay>
  )
}

export default SceneriosForm
